import type { SummaryNodeRaw, SummaryResponse } from 'tio-data/requests/limit';

/**
 * Normalizes a /api/v2/limits/summary response into the model the benefit
 * period cards render: a combined TA + SLR headline with per-product spend
 * segments, sourced from the server-computed tree (paid-only usage, external
 * spend overlaid, binding limits resolved server-side).
 *
 * Amount semantics (all cents, signed — refunds can go negative):
 * - A product node's `annual_used` already INCLUDES external spend attached
 *   anywhere in its subtree (the server rolls children + node-local external
 *   into `used`/`annual_used`).
 * - `external_annual_used` is node-local, so a product's external total is the
 *   sum over its subtree, and its internal (paid) spend is the difference.
 */

export const CREDITS_CAPACITY = 'TAS.LimitCapacity.CREDITS';

const PRODUCT_KEYS = ['TA', 'SLR'] as const;
export type BenefitProduct = (typeof PRODUCT_KEYS)[number];

export type BenefitPeriodMode = 'remaining' | 'used-only' | 'empty';

export interface BenefitPeriodSegment {
  key: 'ta' | 'slr' | 'external';
  /** Signed amount for legend/popover display. */
  rawAmountCents: number;
  /** Clamped to >= 0 for bar-width math. */
  barAmountCents: number;
}

export interface BenefitPeriodProduct {
  product: BenefitProduct;
  /** Includes external spend in the product subtree (server rollup). */
  annualUsedCents: number;
  /** Sum of node-local external_annual_used across the product subtree. */
  externalAnnualUsedCents: number;
  /** annualUsedCents minus the subtree external total. */
  internalAnnualUsedCents: number;
  effectiveAnnualLimitCents: number | null;
  /** Server-computed headroom; null means the product is unbounded. */
  remainingCents: number | null;
}

export interface BenefitPeriodCardModel {
  mode: BenefitPeriodMode;
  /** Combined remaining across TA + SLR; null when unbounded (or used-only/empty). */
  remainingCents: number | null;
  /** Clamped for the headline: never negative, capped at eligibleUpToCents. */
  displayRemainingCents: number | null;
  isOverLimit: boolean;
  /** Combined annual cap; null means no annual cap applies ("no limit"). */
  eligibleUpToCents: number | null;
  /** True when an eligible product has no limit at all while others are capped. */
  hasUnboundedProduct: boolean;
  /** Sum of the segments' clamped amounts — the bar's filled total. */
  totalUsedBarCents: number;
  /** Signed combined annual usage (internal + external) for used-only display. */
  totalUsedCents: number;
  segments: BenefitPeriodSegment[];
  annualStartDate: string | null;
  annualEndDate: string | null;
  /** Pass back as the `date` param to fetch the next period (== annualEndDate). */
  nextWindowFetchDate: string | null;
  /** True when SLR draws from a shared company cap and is shown alongside TA. */
  combinedWithSlr: boolean;
  /**
   * Eligible TA programs have different caps; the product-level figure is not any one program's
   * cap.
   */
  coverageVariesByProgram: boolean;
  products: BenefitPeriodProduct[];
}

const EMPTY_MODEL: BenefitPeriodCardModel = {
  mode: 'empty',
  remainingCents: null,
  displayRemainingCents: null,
  isOverLimit: false,
  eligibleUpToCents: null,
  hasUnboundedProduct: false,
  totalUsedBarCents: 0,
  totalUsedCents: 0,
  segments: [],
  annualStartDate: null,
  annualEndDate: null,
  nextWindowFetchDate: null,
  combinedWithSlr: false,
  coverageVariesByProgram: false,
  products: [],
};

/** Sum node-local external_annual_used over a node and all of its descendants. */
function sumSubtreeExternalAnnual(
  tree: Record<string, SummaryNodeRaw>,
  nodeId: string,
  seen = new Set<string>()
): number {
  const node = tree[nodeId];
  if (!node || seen.has(nodeId)) return 0;
  seen.add(nodeId);

  let total = node.external_annual_used ?? 0;
  for (const childId of node.children ?? []) {
    total += sumSubtreeExternalAnnual(tree, childId, seen);
  }
  return total;
}

/** Collect the distinct caps of the eligible leaf programs under a node. */
function collectEligibleLeafCaps(
  tree: Record<string, SummaryNodeRaw>,
  nodeId: string,
  caps: Set<number | 'unlimited'>,
  seen = new Set<string>()
): void {
  const node = tree[nodeId];
  if (!node || seen.has(nodeId)) return;
  seen.add(nodeId);

  const children = node.children ?? [];
  if (children.length === 0) {
    if (node.eligible === true) {
      caps.add(node.effective_annual_limit ?? node.effective_limit ?? 'unlimited');
    }
    return;
  }
  for (const childId of children) {
    collectEligibleLeafCaps(tree, childId, caps, seen);
  }
}

/**
 * A product participates in the cards when the employee is eligible for it or
 * it carries usage (the server only writes usage keys onto such nodes).
 */
function isActiveProduct(node: SummaryNodeRaw | undefined): node is SummaryNodeRaw {
  return !!node && (node.eligible === true || node.annual_used !== undefined);
}

function sumOrNull(values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);
  if (present.length === 0) return null;
  return present.reduce((total, value) => total + value, 0);
}

/** Primary figure bounded by an optional ceiling; falls back to the ceiling when absent. */
function minOrFallback(primary: number | null, ceiling: number | null): number | null {
  if (primary === null) return ceiling;
  if (ceiling === null) return primary;
  return Math.min(primary, ceiling);
}

export function buildBenefitPeriodCardModel(
  response: SummaryResponse | null | undefined
): BenefitPeriodCardModel {
  if (!response?.tree) return EMPTY_MODEL;

  const { tree, summary, meta } = response;

  // Product-less external rows attach at the company root; its own
  // external_annual_used is node-local and outside both product subtrees.
  const rootId = String(meta.company_id);
  const rootNode = tree[rootId] ?? Object.values(tree).find((node) => node?.level === 0);
  const rootExternal = rootNode?.external_annual_used ?? 0;

  // SLR only affects the tuition-assistance picture when a configured company
  // cap pools the products into one budget; an independently-limited (or
  // unlimited) SLR benefit has no bearing on TA remaining, so it stays off
  // the card entirely.
  const hasSharedCompanyCap = rootNode?.db_limit != null || rootNode?.db_annual_limit != null;

  const products: BenefitPeriodProduct[] = [];
  for (const key of PRODUCT_KEYS) {
    if (key === 'SLR' && !hasSharedCompanyCap) continue;
    const node = tree[key];
    if (!isActiveProduct(node)) continue;

    const annualUsed = node.annual_used ?? 0;
    const externalAnnualUsed = sumSubtreeExternalAnnual(tree, key);
    products.push({
      product: key,
      annualUsedCents: annualUsed,
      externalAnnualUsedCents: externalAnnualUsed,
      internalAnnualUsedCents: annualUsed - externalAnnualUsed,
      effectiveAnnualLimitCents: node.effective_annual_limit ?? null,
      remainingCents: node.remaining ?? null,
    });
  }

  const combinedExternal =
    products.reduce((total, product) => total + product.externalAnnualUsedCents, 0) + rootExternal;

  const summaryAnnualUsed = summary?.annual_used;
  if (products.length === 0 && (!hasSharedCompanyCap || summaryAnnualUsed === undefined)) {
    return EMPTY_MODEL;
  }

  // The company rollup's usage includes SLR and root-attached external spend,
  // so it is only the right total when the products genuinely share that pool.
  const totalUsedCents =
    hasSharedCompanyCap && summaryAnnualUsed !== undefined
      ? summaryAnnualUsed
      : products.reduce((total, product) => total + product.annualUsedCents, 0) + rootExternal;

  const segments: BenefitPeriodSegment[] = [];
  for (const product of products) {
    segments.push({
      key: product.product === 'TA' ? 'ta' : 'slr',
      rawAmountCents: product.internalAnnualUsedCents,
      barAmountCents: Math.max(0, product.internalAnnualUsedCents),
    });
  }
  if (combinedExternal !== 0) {
    segments.push({
      key: 'external',
      rawAmountCents: combinedExternal,
      barAmountCents: Math.max(0, combinedExternal),
    });
  }

  // The employee's personally-accessible benefit is the product view. The
  // company rollup can span budgets the employee cannot draw themselves —
  // e.g. dependent programs sharing a company-wide cap — so it only tightens
  // the figure as a ceiling (and only when that shared cap exists); it never
  // replaces the product view.
  const remainingCents = minOrFallback(
    sumOrNull(products.map((product) => product.remainingCents)),
    hasSharedCompanyCap ? (summary?.remaining ?? null) : null
  );
  const eligibleUpToCents = minOrFallback(
    sumOrNull(products.map((product) => product.effectiveAnnualLimitCents)),
    hasSharedCompanyCap ? (summary?.effective_annual_limit ?? null) : null
  );
  const hasUnboundedProduct = products.some((product) => product.remainingCents === null);

  const isOverLimit = remainingCents !== null && remainingCents < 0;
  let displayRemainingCents: number | null = null;
  if (remainingCents !== null) {
    displayRemainingCents = Math.max(
      0,
      eligibleUpToCents !== null ? Math.min(remainingCents, eligibleUpToCents) : remainingCents
    );
  }

  let coverageVariesByProgram = false;
  if (products.some((product) => product.product === 'TA')) {
    const caps = new Set<number | 'unlimited'>();
    collectEligibleLeafCaps(tree, 'TA', caps);
    coverageVariesByProgram = caps.size > 1;
  }

  return {
    mode: remainingCents !== null ? 'remaining' : 'used-only',
    remainingCents,
    displayRemainingCents,
    isOverLimit,
    eligibleUpToCents,
    hasUnboundedProduct,
    totalUsedBarCents: segments.reduce((total, segment) => total + segment.barAmountCents, 0),
    totalUsedCents,
    segments,
    annualStartDate: meta.annual_start_date ?? null,
    annualEndDate: meta.annual_end_date ?? null,
    nextWindowFetchDate: meta.annual_end_date ?? null,
    combinedWithSlr: products.some((product) => product.product === 'SLR'),
    coverageVariesByProgram,
    products,
  };
}

/**
 * Mirrors the existing next-year card rule: show the next period once its
 * window starts within 6 months of now.
 */
export function shouldShowNextWindow(
  annualEndDate: string | null | undefined,
  now = new Date()
): boolean {
  if (!annualEndDate) return false;
  const nextStart = new Date(annualEndDate);
  if (Number.isNaN(nextStart.getTime())) return false;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() + 6);
  return nextStart <= cutoff;
}

interface TemplateWithFields {
  fields?: Record<string, { values?: unknown[] } | null | undefined> | null;
}

/**
 * Credit-capacity programs limit by course credits, which the limits tree does
 * not model — surfaces keep the legacy status-based display for them.
 */
export function templatesIncludeCreditsCapacity(
  templates: readonly TemplateWithFields[] | null | undefined
): boolean {
  return !!templates?.some(
    (template) => template?.fields?.['LIMIT_CAPACITY']?.values?.[0] === CREDITS_CAPACITY
  );
}
